import React from 'react';
import Tooltip from '@mui/material/Tooltip';

interface BannerStylesCustomizationProps {
    primaryTextColor: string;
    setPrimaryTextColor: (val: string) => void;
    secondaryTextColor: string;
    setSecondaryTextColor: (val: string) => void;
    buttonColor: string;
    setButtonColor: (val: string) => void;
    buttonTextColor: string;
    setButtonTextColor: (val: string) => void;
    bannerBackgroundColor: string;
    setBannerBackgroundColor: (val: string) => void;
    textAlignment: string;
    setTextAlignment: (val: string) => void;
    buttonAlignment: string;
    setButtonAlignment: (val: string) => void;
    layout: 'column' | 'row';
    setLayout: (val: 'column' | 'row') => void;
    hasButton: boolean;
    disabled: boolean;
}

const BannerStylesCustomization: React.FC<BannerStylesCustomizationProps> = ({
    primaryTextColor, setPrimaryTextColor,
    secondaryTextColor, setSecondaryTextColor,
    buttonColor, setButtonColor,
    buttonTextColor, setButtonTextColor,
    bannerBackgroundColor, setBannerBackgroundColor,
    textAlignment, setTextAlignment,
    buttonAlignment, setButtonAlignment,
    layout, setLayout,
    hasButton,
    disabled
}) => {
    const [activeFocus, setActiveFocus] = React.useState<string | null>(null);

    const handleReceiveFocus = (element: string) => setActiveFocus(element);
    const handleMouseEnter = (element: string) => { if (activeFocus && activeFocus !== element) setActiveFocus(null); };
    const handleMouseLeave = () => { if (!activeFocus) setActiveFocus(null); };

    const alignments = ['Left', 'Center', 'Right'];
    const buttonDisabledTooltip = "Select 'Show a button' to enable this option.";

    return (
        <div className="banner-styles-section">

            {/* Banner Colors */}
            <div className="bl-section-card">
                <div className="bl-section-card-header">
                    <h4>Banner Colors</h4>
                </div>
                <div className="bl-section-card-body">
                    <div className="banner-input-wrapper">
                        <div className="color-input-wrapper-banner">
                            <label className="color" onMouseEnter={() => handleMouseEnter('bannerBg')} onMouseLeave={handleMouseLeave}>
                                <p>Background</p>
                                <input type="color" value={bannerBackgroundColor} onChange={(e) => setBannerBackgroundColor(e.target.value)} disabled={disabled} onFocus={() => handleReceiveFocus('bannerBg')} onClick={() => handleReceiveFocus('bannerBg')} />
                            </label>
                            <label className="color" onMouseEnter={() => handleMouseEnter('primaryText')} onMouseLeave={handleMouseLeave}>
                                <p>Primary Text</p>
                                <input type="color" value={primaryTextColor} onChange={(e) => setPrimaryTextColor(e.target.value)} disabled={disabled} onFocus={() => handleReceiveFocus('primaryText')} onClick={() => handleReceiveFocus('primaryText')} />
                            </label>
                            <label className="color" onMouseEnter={() => handleMouseEnter('secondaryText')} onMouseLeave={handleMouseLeave}>
                                <p>Secondary Text</p>
                                <input type="color" value={secondaryTextColor} onChange={(e) => setSecondaryTextColor(e.target.value)} disabled={disabled} onFocus={() => handleReceiveFocus('secondaryText')} onClick={() => handleReceiveFocus('secondaryText')} />
                            </label>
                            <Tooltip title={!hasButton ? buttonDisabledTooltip : ''} arrow placement="bottom">
                                <span className="bl-lockable-wrapper" style={{ display: 'inline-flex', gap: '12px' }}>
                                    <label className={`color${!hasButton ? ' bl-section-locked' : ''}`} onMouseEnter={() => handleMouseEnter('buttonBg')} onMouseLeave={handleMouseLeave}>
                                        <p>Button Bg</p>
                                        <input type="color" value={buttonColor} onChange={(e) => setButtonColor(e.target.value)} disabled={disabled || !hasButton} onFocus={() => handleReceiveFocus('buttonBg')} onClick={() => handleReceiveFocus('buttonBg')} />
                                    </label>
                                    <label className={`color${!hasButton ? ' bl-section-locked' : ''}`} onMouseEnter={() => handleMouseEnter('buttonText')} onMouseLeave={handleMouseLeave}>
                                        <p>Button Text</p>
                                        <input type="color" value={buttonTextColor} onChange={(e) => setButtonTextColor(e.target.value)} disabled={disabled || !hasButton} onFocus={() => handleReceiveFocus('buttonText')} onClick={() => handleReceiveFocus('buttonText')} />
                                    </label>
                                </span>
                            </Tooltip>
                        </div>
                    </div>
                </div>
            </div>

            {/* Layout & Alignment */}
            <div className="bl-section-card">
                <div className="bl-section-card-header">
                    <h4>Layout &amp; Alignment</h4>
                </div>
                <div className="bl-section-card-body">

                    {/* Banner Layout */}
                    <Tooltip title={!hasButton ? buttonDisabledTooltip : ''} arrow placement="bottom">
                        <span className="bl-lockable-wrapper">
                            <div className={`field-group${!hasButton ? ' bl-section-locked' : ''}`}>
                                <p className="field-label"><b>Banner layout</b></p>
                                <p className="field-hint">How text and button are arranged on desktop. On mobile, content is always stacked.</p>
                                <div className="bl-option-cards">
                                    <label className={`bl-option-card ${layout === 'column' ? 'selected' : ''}`}>
                                        <input type="radio" name="layout" value="column" checked={layout === 'column'} onChange={() => setLayout('column')} disabled={disabled || !hasButton} />
                                        <div className="bl-option-card-inner">
                                            <div className="bl-option-card-header-row bl-option-card-header-row--spread">
                                                <span className="bl-option-card-title">Stacked</span>
                                                <svg width="30" height="22" viewBox="0 0 30 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <rect x="1" y="1" width="28" height="4" rx="1.5" fill="#BDD0EA" />
                                                    <rect x="1" y="9" width="18" height="4" rx="1.5" fill="#BDD0EA" />
                                                    <rect x="1" y="17" width="10" height="4" rx="1.5" fill="#5493DA" />
                                                </svg>
                                            </div>
                                            <span className="bl-option-card-desc">Text above the button.</span>
                                        </div>
                                    </label>
                                    <label className={`bl-option-card ${layout === 'row' ? 'selected' : ''}`}>
                                        <input type="radio" name="layout" value="row" checked={layout === 'row'} onChange={() => setLayout('row')} disabled={disabled || !hasButton} />
                                        <div className="bl-option-card-inner">
                                            <div className="bl-option-card-header-row bl-option-card-header-row--spread">
                                                <span className="bl-option-card-title">Inline</span>
                                                <svg width="30" height="22" viewBox="0 0 30 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <rect x="1" y="4" width="14" height="3.5" rx="1.5" fill="#BDD0EA" />
                                                    <rect x="1" y="10" width="10" height="3.5" rx="1.5" fill="#BDD0EA" />
                                                    <rect x="18" y="4" width="11" height="12" rx="2" fill="#5493DA" />
                                                </svg>
                                            </div>
                                            <span className="bl-option-card-desc">Text and button on the same line.</span>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </span>
                    </Tooltip>

                    {/* Text & Button Alignment */}
                    <div className="row mt-4">
                        <div className="col-sm-6 col-left">
                            <div className="field-group">
                                <p className="field-label"><b>Text alignment</b></p>
                                <div className="bl-alignment-seg">
                                    {alignments.map((opt) => (
                                        <button key={opt} type="button" className={`bl-alignment-btn ${textAlignment === opt.toLowerCase() ? 'active' : ''}`} onClick={() => setTextAlignment(opt.toLowerCase())} disabled={disabled}>
                                            {opt}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="col-sm-6 col-right">
                            <Tooltip title={!hasButton ? buttonDisabledTooltip : ''} arrow placement="bottom">
                                <span className="bl-lockable-wrapper">
                                    <div className={`field-group${!hasButton ? ' bl-section-locked' : ''}`}>
                                        <p className="field-label"><b>Button alignment</b></p>
                                        <div className="bl-alignment-seg">
                                            {alignments.map((opt) => (
                                                <button key={opt} type="button" className={`bl-alignment-btn ${buttonAlignment === opt.toLowerCase() ? 'active' : ''}`} onClick={() => setButtonAlignment(opt.toLowerCase())} disabled={disabled || !hasButton}>
                                                    {opt}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </span>
                            </Tooltip>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BannerStylesCustomization;